/**
 * Token utility functions for authentication
 * Handles JWT token validation, expiration checks, and session management
 */

/**
 * Decode a JWT token without verification (client-side only)
 * @param {string} token - JWT token string
 * @returns {object|null} Decoded payload or null if invalid
 */
export const decodeToken = (token) => {
  if (!token) return null;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error('Token decode error:', error);
    return null;
  }
};

/**
 * Check if a token is expired
 * @param {string} token - JWT token string
 * @returns {boolean} True if token is expired or invalid
 */
export const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();
  
  // Add a small buffer (30 seconds) to handle clock skew
  const buffer = 30 * 1000;
  
  return currentTime >= (expirationTime - buffer);
};

/**
 * Get time until token expiration
 * @param {string} token - JWT token string
 * @returns {number} Milliseconds until expiration, or -1 if expired/invalid
 */
export const getTokenExpirationTime = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return -1;
  
  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();
  const remainingTime = expirationTime - currentTime;
  
  return remainingTime > 0 ? remainingTime : -1;
};

/**
 * Check if token exists and is valid
 * @returns {boolean} True if valid token exists
 */
export const hasValidToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  return !isTokenExpired(token);
};

/**
 * Get the stored token if it's valid, otherwise clear it
 * @returns {string|null} Valid token or null
 */
export const getValidToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  if (isTokenExpired(token)) {
    // Clear expired token
    localStorage.removeItem('token');
    return null;
  }
  
  return token;
};

/**
 * Clear all authentication data from storage
 */
export const clearAuthData = () => {
  localStorage.removeItem('token');
  // Clear any other auth-related data
  sessionStorage.clear();
};

/**
 * Session timeout duration (24 hours in milliseconds)
 */
export const SESSION_TIMEOUT = 24 * 60 * 60 * 1000;

/**
 * Check if session has timed out based on last activity
 * @returns {boolean} True if session has timed out
 */
export const isSessionTimedOut = () => {
  const lastActivity = localStorage.getItem('lastActivity');
  if (!lastActivity) return false;
  
  const elapsed = Date.now() - parseInt(lastActivity, 10);
  return elapsed > SESSION_TIMEOUT;
};

/**
 * Update last activity timestamp
 */
export const updateLastActivity = () => {
  localStorage.setItem('lastActivity', Date.now().toString());
};

const tokenUtils = {
  decodeToken,
  isTokenExpired,
  getTokenExpirationTime,
  hasValidToken,
  getValidToken,
  clearAuthData,
  isSessionTimedOut,
  updateLastActivity,
  SESSION_TIMEOUT
};

export default tokenUtils;
